import { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { apiClient, ApiError } from '../lib/apiClient';
import { UnifiedStatusBadge } from './unified';
import StatusActions from './StatusActions';
import ContextSelector from './ContextSelector';
import TableView from './TableView';
import { Sheet } from './overlay';
import { AnimatedList } from './motion';

const APPROVAL_ROLES = new Set(['owner', 'tenant_admin', 'alcedo_operator', 'admin']);

function getRoleFromToken(token) {
  if (!token) return null;
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    return JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/'))).role || null;
  } catch {
    return null;
  }
}

const PRIORITY_COLORS = {
  HIGH: 'var(--aif-color-danger)',
  MEDIUM: 'var(--aif-color-warning)',
  LOW: 'var(--aif-text-muted)',
};

const PRIORITY_LABELS = {
  HIGH: 'task.priorityHigh',
  MEDIUM: 'task.priorityMedium',
  LOW: 'task.priorityLow',
};

const WEATHER_DOT_COLORS = {
  rainy: 'var(--aif-color-info)',
  cloudy: 'var(--aif-text-muted)',
  windy: 'var(--aif-color-accent)',
  sunny: 'var(--aif-color-warning)',
};

// Small colored dot for status — uses CSS vars via inline style for theme support
const STATUS_DOT_STYLES = {
  DRAFT:     'var(--aif-status-draft)',
  SUBMITTED: 'var(--aif-status-submitted)',
  APPROVED:  'var(--aif-status-approved)',
  POSTED:    'var(--aif-status-posted)',
  ARCHIVED:  'var(--aif-status-archived)',
};

function StatusDot({ status }) {
  const color = STATUS_DOT_STYLES[status] || STATUS_DOT_STYLES.DRAFT;
  return <span className="inline-block w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: color }} title={status} />;
}

const ACTIVITY_TYPE_OPTIONS = [
  'FERTILIZATION',
  'CROP_PROTECTION',
  'TILLAGE',
  'SOWING',
  'HARVEST',
  'OTHER',
];

const ACTIVITY_TYPE_ICONS = {
  FERTILIZATION: '🌿',
  CROP_PROTECTION: '💧',
  TILLAGE: '🚜',
  SOWING: '🌱',
  HARVEST: '🌾',
  OTHER: '📋',
};

// ── Helpers ──────────────────────────────────────────────────────────────────

function fmtDate(iso, locale = 'de-DE') {
  if (!iso) return '';
  const d = new Date(iso + 'T00:00:00');
  return d.toLocaleDateString(locale, { day: '2-digit', month: '2-digit' });
}

// ── Task Form ────────────────────────────────────────────────────────────────

function TaskForm({ token, fields, onSave, onCancel, initialDate, initialFieldId }) {
  const { t } = useTranslation();
  const [title, setTitle] = useState('');
  const [fieldId, setFieldId] = useState(initialFieldId || '');
  const [dueDate, setDueDate] = useState(initialDate || '');
  const [priority, setPriority] = useState('MEDIUM');
  const [activityType, setActivityType] = useState('');
  const [weatherDependent, setWeatherDependent] = useState(false);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  // Progressive disclosure: expanded details section
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [machineId, setMachineId] = useState('');
  const [implementId, setImplementId] = useState('');
  const [productName, setProductName] = useState('');
  const [quantity, setQuantity] = useState('');
  const [quantityUnit, setQuantityUnit] = useState('kg/ha');
  const [machines, setMachines] = useState([]);
  const [implements_, setImplements] = useState([]);

  // Fetch machines + implements on mount
  useEffect(() => {
    if (!token) return;
    apiClient.get('/api/v1/aif/equipment/machines')
      .then(r => setMachines(r.data?.machines || [])).catch(() => {});
    apiClient.get('/api/v1/aif/equipment/implements')
      .then(r => setImplements(r.data?.implements || [])).catch(() => {});
  }, [token]);

  // Auto-expand details for PSM tasks
  useEffect(() => {
    if (activityType === 'CROP_PROTECTION') setDetailsOpen(true);
  }, [activityType]);

  // Filter implements by selected machine type
  const selectedMachine = machines.find(m => m.id === machineId);
  const filteredImplements = selectedMachine
    ? implements_.filter(i => i.compatible_machine_types?.includes(selectedMachine.machine_type))
    : implements_;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim()) return;
    setSaving(true);
    try {
      const body = { title: title.trim(), priority };
      if (fieldId) body.field_id = fieldId;
      if (dueDate) body.due_date = dueDate;
      if (activityType) body.activity_type = activityType;
      if (weatherDependent) body.weather_dependent = true;
      if (notes.trim()) body.notes = notes.trim();
      if (machineId) body.machine_id = machineId;
      if (implementId) body.implement_id = implementId;
      if (productName.trim()) body.product_name = productName.trim();
      if (quantity) body.planned_quantity = parseFloat(quantity);
      if (quantityUnit) body.planned_unit = quantityUnit;

      try {
        const result = await apiClient.post('/api/v1/aif/farm/tasks', body, {
          entityType: 'task',
          queueOnOffline: true,
        });
        if (!result.queued) onSave(result.data);
      } catch (err) {
        if (!(err instanceof ApiError)) throw err;
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="bg-[var(--aif-bg-surface)] border border-[var(--aif-border-default)] rounded-2xl p-4 space-y-3"
      data-testid="task-form"
    >
      <input
        type="text"
        value={title}
        onChange={e => setTitle(e.target.value)}
        placeholder={t('task.titlePlaceholder')}
        className="w-full text-sm bg-[var(--aif-bg-input)] border border-[var(--aif-border-default)] rounded-xl px-3 py-2 text-[var(--aif-text-primary)] placeholder-[var(--aif-text-muted)] focus:outline-none focus:border-[var(--aif-color-primary)]"
        required
        data-testid="task-title-input"
      />

      <div className="space-y-2">
        <ContextSelector
          label={t('task.fieldLabel')}
          value={fieldId || null}
          options={(fields || []).map(f => ({ id: f.id, label: f.name, sublabel: f.area_ha ? `${f.area_ha} ha` : undefined }))}
          onChange={id => setFieldId(id || '')}
          placeholder={t('task.fieldNone')}
          allowClear={!!fieldId}
        />
        <div>
          <label className="text-xs text-[var(--aif-text-muted)] mb-1 block">{t('task.dueDateLabel')}</label>
          <input
            type="date"
            value={dueDate}
            onChange={e => setDueDate(e.target.value)}
            className="w-full text-xs bg-[var(--aif-bg-input)] border border-[var(--aif-border-default)] rounded-xl px-2 py-1.5 text-[var(--aif-text-primary)] focus:outline-none min-h-[44px]"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="text-xs text-[var(--aif-text-muted)] mb-1 block">{t('task.priorityLabel')}</label>
          <select
            value={priority}
            onChange={e => setPriority(e.target.value)}
            className="w-full text-xs bg-[var(--aif-bg-input)] border border-[var(--aif-border-default)] rounded-xl px-2 py-1.5 text-[var(--aif-text-primary)] focus:outline-none"
          >
            <option value="HIGH">{t('task.priorityHigh')}</option>
            <option value="MEDIUM">{t('task.priorityMedium')}</option>
            <option value="LOW">{t('task.priorityLow')}</option>
          </select>
        </div>
        <div>
          <label className="text-xs text-[var(--aif-text-muted)] mb-1 block">{t('task.activityTypeLabel')}</label>
          <select
            value={activityType}
            onChange={e => setActivityType(e.target.value)}
            className="w-full text-xs bg-[var(--aif-bg-input)] border border-[var(--aif-border-default)] rounded-xl px-2 py-1.5 text-[var(--aif-text-primary)] focus:outline-none"
          >
            <option value="">—</option>
            {ACTIVITY_TYPE_OPTIONS.map(type => (
              <option key={type} value={type}>{ACTIVITY_TYPE_ICONS[type]} {type}</option>
            ))}
          </select>
        </div>
      </div>

      <label className="flex items-center gap-2 text-xs text-[var(--aif-text-primary)] cursor-pointer">
        <input
          type="checkbox"
          checked={weatherDependent}
          onChange={e => setWeatherDependent(e.target.checked)}
          className="rounded"
          data-testid="weather-dependent-checkbox"
        />
        {t('task.weatherDependentLabel')}
      </label>

      {/* Progressive disclosure: Details (Machine, Implement, Product) */}
      <button
        type="button"
        onClick={() => setDetailsOpen(!detailsOpen)}
        className="w-full text-left text-xs text-[var(--aif-color-primary)] font-medium py-1 flex items-center gap-1"
        data-testid="task-details-toggle"
      >
        <span className="transition-transform" style={{ transform: detailsOpen ? 'rotate(90deg)' : '' }}>▶</span>
        {t('weekly.detailsToggle')}
      </button>

      {detailsOpen && (
        <div className="space-y-2 bg-[var(--aif-bg-elevated)] rounded-xl p-3 border border-[var(--aif-border-default)]" data-testid="task-details-section">
          {/* PSM Sachkunde warning */}
          {activityType === 'CROP_PROTECTION' && (
            <div className="text-[10px] text-orange-500 bg-orange-500/10 rounded-lg px-2 py-1.5" data-testid="psm-warning">
              {t('weekly.psmWarning')}
            </div>
          )}

          {/* Machine */}
          <div>
            <label className="text-[10px] text-[var(--aif-text-muted)] mb-0.5 block">{t('weekly.machine')}</label>
            <select
              value={machineId}
              onChange={e => { setMachineId(e.target.value); setImplementId(''); }}
              className="w-full text-xs bg-[var(--aif-bg-input)] border border-[var(--aif-border-default)] rounded-xl px-2 py-1.5 text-[var(--aif-text-primary)] focus:outline-none min-h-[44px]"
              data-testid="task-machine-select"
            >
              <option value="">{t('weekly.noMachine')}</option>
              {machines.map(m => (
                <option key={m.id} value={m.id}>{m.name}{m.power_kw ? ` (${m.power_kw} kW)` : ''}</option>
              ))}
            </select>
          </div>

          {/* Implement (filtered by machine) */}
          <div>
            <label className="text-[10px] text-[var(--aif-text-muted)] mb-0.5 block">{t('weekly.implement')}</label>
            <select
              value={implementId}
              onChange={e => setImplementId(e.target.value)}
              className="w-full text-xs bg-[var(--aif-bg-input)] border border-[var(--aif-border-default)] rounded-xl px-2 py-1.5 text-[var(--aif-text-primary)] focus:outline-none min-h-[44px]"
              data-testid="task-implement-select"
            >
              <option value="">{t('weekly.noImplement')}</option>
              {filteredImplements.map(i => (
                <option key={i.id} value={i.id}>{i.display_name}{i.working_width_m ? ` (${i.working_width_m}m)` : ''}</option>
              ))}
            </select>
          </div>

          {/* Product + Quantity */}
          <div className="grid grid-cols-[1fr_80px_70px] gap-1.5">
            <div>
              <label className="text-[10px] text-[var(--aif-text-muted)] mb-0.5 block">{t('weekly.product')}</label>
              <input
                type="text"
                value={productName}
                onChange={e => setProductName(e.target.value)}
                placeholder={t('weekly.productPlaceholder')}
                className="w-full text-xs bg-[var(--aif-bg-input)] border border-[var(--aif-border-default)] rounded-xl px-2 py-1.5 text-[var(--aif-text-primary)] focus:outline-none min-h-[44px]"
                data-testid="task-product-input"
              />
            </div>
            <div>
              <label className="text-[10px] text-[var(--aif-text-muted)] mb-0.5 block">{t('form.quantity')}</label>
              <input
                type="number"
                value={quantity}
                onChange={e => setQuantity(e.target.value)}
                placeholder={t('weekly.quantityPlaceholder')}
                className="w-full text-xs bg-[var(--aif-bg-input)] border border-[var(--aif-border-default)] rounded-xl px-2 py-1.5 text-[var(--aif-text-primary)] focus:outline-none min-h-[44px]"
                data-testid="task-quantity-input"
              />
            </div>
            <div>
              <label className="text-[10px] text-[var(--aif-text-muted)] mb-0.5 block">{t('form.unit')}</label>
              <select
                value={quantityUnit}
                onChange={e => setQuantityUnit(e.target.value)}
                className="w-full text-xs bg-[var(--aif-bg-input)] border border-[var(--aif-border-default)] rounded-xl px-2 py-1.5 text-[var(--aif-text-primary)] focus:outline-none min-h-[44px]"
              >
                <option value="kg/ha">kg/ha</option>
                <option value="l/ha">l/ha</option>
                <option value="kg">kg</option>
                <option value="l">l</option>
                <option value="t">t</option>
              </select>
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="text-[10px] text-[var(--aif-text-muted)] mb-0.5 block">{t('task.notesLabel')}</label>
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder={t('weekly.notesPlaceholder')}
              rows={2}
              className="w-full text-xs bg-[var(--aif-bg-input)] border border-[var(--aif-border-default)] rounded-xl px-2 py-1.5 text-[var(--aif-text-primary)] focus:outline-none resize-none"
            />
          </div>
        </div>
      )}

      <div className="flex gap-2 pt-1">
        <button
          type="submit"
          disabled={saving || !title.trim()}
          className="flex-1 text-sm font-medium bg-[var(--aif-color-primary)] text-white rounded-xl py-2 disabled:opacity-50"
          data-testid="task-save-button"
        >
          {saving ? '…' : t('task.save')}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="px-4 text-sm text-[var(--aif-text-muted)] hover:text-[var(--aif-text-primary)] transition"
        >
          {t('task.cancel')}
        </button>
      </div>
    </form>
  );
}

// ── Task Card ────────────────────────────────────────────────────────────────

function TaskCard({ task, onPatch, onDelete, weatherIcon, token, onStatusTransition, userRole = 'owner' }) {
  const { t, i18n } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  // "Done" in the universal lifecycle = APPROVED or beyond
  const isDone = ['APPROVED', 'POSTED', 'ARCHIVED'].includes(task.status);

  const handleToggleDone = async (e) => {
    e.stopPropagation();
    const newStatus = isDone ? 'DRAFT' : 'APPROVED';
    await onPatch(task.id, { status: newStatus });
  };

  const handleDelete = async (e) => {
    e.stopPropagation();
    await onDelete(task.id);
  };

  const priorityColor = PRIORITY_COLORS[task.priority] || PRIORITY_COLORS.MEDIUM;

  return (
    <div
      className={`bg-[var(--aif-bg-surface)] border rounded-xl p-3 cursor-pointer transition ${
        isDone ? 'opacity-50 border-[var(--aif-border-default)]' : 'border-[var(--aif-border-default)] hover:border-[var(--aif-color-primary)]'
      }`}
      onClick={() => setExpanded(x => !x)}
      data-testid="task-card"
    >
      <div className="flex items-start gap-2">
        {/* Priority dot */}
        <div className="mt-1 w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: priorityColor }} />

        <div className="flex-1 min-w-0 overflow-hidden">
          <div className="flex items-center gap-1 min-w-0">
            {task.activity_type && (
              <span className="text-xs flex-shrink-0">{ACTIVITY_TYPE_ICONS[task.activity_type] || '📋'}</span>
            )}
            <span className={`text-xs font-medium truncate flex-1 min-w-0 ${isDone ? 'line-through text-[var(--aif-text-muted)]' : 'text-[var(--aif-text-primary)]'}`}>
              {task.title}
            </span>
            <StatusDot status={task.status} />
          </div>
          {(task.field_name || task.due_date) && (
            <div className="flex items-center gap-1.5 mt-0.5">
              {task.field_name && (
                <span className="text-[10px] text-[var(--aif-text-muted)] truncate">{task.field_name}</span>
              )}
              {task.due_date && (
                <span className="text-[10px] text-[var(--aif-text-muted)] flex-shrink-0">{fmtDate(task.due_date, i18n.language)}</span>
              )}
            </div>
          )}
        </div>

        {/* Weather dot */}
        {task.weather_dependent && weatherIcon && (
          <div
            className="w-3 h-3 rounded-full flex-shrink-0 mt-1"
            style={{ backgroundColor: WEATHER_DOT_COLORS[weatherIcon] || 'var(--aif-color-warning)' }}
            title={t('weekly.weatherDot')}
            data-testid="weather-dot"
          />
        )}

        {/* Done checkbox */}
        <button
          onClick={handleToggleDone}
          className={`w-5 h-5 rounded-full border-2 flex-shrink-0 flex items-center justify-center transition ${
            isDone
              ? 'bg-[var(--aif-color-success)] border-[var(--aif-color-success)] text-white'
              : 'border-[var(--aif-border-default)] hover:border-[var(--aif-color-primary)]'
          }`}
          data-testid="task-done-button"
        >
          {isDone && (
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
            </svg>
          )}
        </button>
      </div>

      {expanded && (
        <div className="mt-2 pl-4 space-y-2">
          <UnifiedStatusBadge status={task.status} />
          {task.notes && (
            <p className="text-xs text-[var(--aif-text-muted)]">{task.notes}</p>
          )}
          {token && (
            <StatusActions
              eventTable="aif_farm_tasks"
              eventId={task.id}
              currentStatus={task.status}
              userRole={userRole}
              token={token}
              onTransition={onStatusTransition}
              data-testid="task-status-actions"
            />
          )}
          <button
            onClick={handleDelete}
            className="text-xs text-red-400 hover:text-red-500 transition"
            data-testid="task-delete-button"
          >
            {t('task.delete')}
          </button>
        </div>
      )}
    </div>
  );
}

// ── Day Column ────────────────────────────────────────────────────────────────

function DayColumn({ day, onPatch, onDelete, token, onStatusTransition }) {
  const { t } = useTranslation();
  const weatherIcon = day.weather?.icon;

  return (
    <div className="min-w-0" data-testid="day-column">
      {/* Day header */}
      <div className="mb-2 text-center">
        <div className={`text-xs font-semibold ${day.is_today ? 'text-[var(--aif-color-primary)]' : 'text-[var(--aif-text-muted)]'}`}>
          {day.day_name ? day.day_name.slice(0, 2).toUpperCase() : ''}
        </div>
        <div className={`text-sm font-bold ${day.is_today ? 'text-[var(--aif-color-primary)]' : 'text-[var(--aif-text-primary)]'}`}>
          {day.date ? new Date(day.date + 'T00:00:00').getDate() : ''}
        </div>
        {/* Weather icon */}
        {weatherIcon && (
          <div
            className="w-2 h-2 rounded-full mx-auto mt-1"
            style={{ backgroundColor: WEATHER_DOT_COLORS[weatherIcon] || 'var(--aif-color-warning)' }}
            title={weatherIcon}
          />
        )}
      </div>

      {/* Tasks */}
      {day.tasks && day.tasks.length > 0 ? (
        <AnimatedList className="space-y-1.5">
          {day.tasks.map(task => (
            <TaskCard
              key={task.id}
              task={task}
              onPatch={onPatch}
              onDelete={onDelete}
              weatherIcon={weatherIcon}
              token={token}
              onStatusTransition={onStatusTransition}
            />
          ))}
        </AnimatedList>
      ) : (
        <div className="text-center text-xs text-[var(--aif-text-muted)] py-2">
          {t('weekly.noTasksDay')}
        </div>
      )}
    </div>
  );
}

// ── Overdue Section ───────────────────────────────────────────────────────────

function OverdueSection({ tasks, onPatch, onDelete }) {
  const { t } = useTranslation();
  if (!tasks || tasks.length === 0) return null;

  return (
    <div className="mb-6" data-testid="overdue-section">
      <h3 className="text-sm font-semibold text-red-400 mb-2 flex items-center gap-1.5">
        <span>⚠️</span>
        <span>{t('weekly.overdueSection')}</span>
        <span className="ml-1 px-1.5 py-0.5 rounded-full bg-red-100 text-red-500 text-xs font-bold">{tasks.length}</span>
      </h3>
      <div className="space-y-2">
        {tasks.map(task => (
          <div key={task.id} className="bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800 rounded-xl p-3">
            <div className="flex items-start gap-2">
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-[var(--aif-text-primary)] truncate">{task.title}</div>
                <div className="flex items-center gap-2 mt-0.5">
                  {task.field_name && (
                    <span className="text-xs text-[var(--aif-text-muted)]">{task.field_name}</span>
                  )}
                  <span className="text-xs text-red-400">
                    {t('task.daysOverdue', { count: task.days_overdue || 0 })}
                  </span>
                </div>
              </div>
              <button
                onClick={() => onPatch(task.id, { status: 'APPROVED' })}
                className="text-xs bg-red-100 hover:bg-red-200 dark:bg-red-900/30 text-red-500 px-2 py-1 rounded-lg transition"
              >
                {t('task.markDone')}
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Suggested Tasks ────────────────────────────────────────────────────────────

function SuggestedSection({ tasks, token, onAdded }) {
  const { t } = useTranslation();
  if (!tasks || tasks.length === 0) return null;

  const handleAdd = async (suggestion) => {
    const body = {
      title: suggestion.title,
      activity_type: suggestion.activity_type,
      due_date: suggestion.due_date,
    };
    if (suggestion.field_id) body.field_id = suggestion.field_id;

    try {
      await apiClient.post('/api/v1/aif/farm/tasks', body, {
        entityType: 'task',
        queueOnOffline: true,
      });
      onAdded();
    } catch (err) {
      if (!(err instanceof ApiError)) throw err;
    }
  };

  return (
    <div className="mb-6" data-testid="suggested-section">
      <h3 className="text-sm font-semibold text-[var(--aif-color-primary)] mb-2 flex items-center gap-1.5">
        <span>✨</span>
        <span>{t('weekly.suggestedSection')}</span>
      </h3>
      <div className="space-y-2">
        {tasks.map((s, i) => (
          <div key={i} className="bg-[var(--aif-bg-surface)] border border-[var(--aif-border-default)] rounded-xl p-3 flex items-center gap-2">
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-[var(--aif-text-primary)] truncate">{s.title}</div>
              {s.field && <div className="text-xs text-[var(--aif-text-muted)] truncate">{s.field}</div>}
            </div>
            <button
              onClick={() => handleAdd(s)}
              className="text-xs font-medium text-[var(--aif-color-primary)] border border-[var(--aif-color-primary)] px-2 py-1 rounded-lg hover:bg-[var(--aif-color-primary)] hover:text-white transition"
              data-testid="suggested-add-button"
            >
              {t('task.addSuggested')}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Summary Bar ────────────────────────────────────────────────────────────────

function SummaryBar({ summary }) {
  const { t } = useTranslation();
  if (!summary) return null;

  return (
    <div className="flex gap-4 text-xs text-[var(--aif-text-muted)] mb-4" data-testid="summary-bar">
      <span className="text-[var(--aif-text-primary)] font-medium">{summary.open_tasks ?? 0} {t('task.statusOpen').toLowerCase()}</span>
      <span>{summary.done_this_week ?? 0} {t('task.statusDone').toLowerCase()}</span>
      {summary.overdue_count > 0 && (
        <span className="text-red-400">{summary.overdue_count} {t('task.overdue').toLowerCase()}</span>
      )}
    </div>
  );
}

// ── Task Table (Tabelle view) ──────────────────────────────────────────────────

// TASK_TYPE_LABELS — built dynamically via useTaskTypeLabels() so t() works
function useTaskTypeLabels() {
  const { t } = useTranslation();
  return useMemo(() => ({
    FERTILIZATION: t('weekly.taskTypes.fertilization'),
    CROP_PROTECTION: t('weekly.taskTypes.cropProtection'),
    TILLAGE: t('weekly.taskTypes.tillage'),
    SOWING: t('weekly.taskTypes.sowing'),
    HARVEST: t('weekly.taskTypes.harvest'),
    OTHER: t('weekly.taskTypes.other'),
  }), [t]);
}

const PRIORITY_ORDER = { HIGH: 0, MEDIUM: 1, LOW: 2 };

function useTaskColumns() {
  const { t, i18n } = useTranslation();
  const typeLabels = useTaskTypeLabels();
  return useMemo(() => [
    {
      key: 'status',
      label: t('weekly.columns.status'),
      render: row => <UnifiedStatusBadge status={row.status} />,
      sortable: true,
    },
    {
      key: 'title',
      label: t('weekly.columns.what'),
      render: row => (
        <div>
          <div className="text-sm text-[var(--aif-text-primary)] font-medium">{row.title}</div>
          {row.activity_type && (
            <div className="text-xs text-[var(--aif-text-muted)]">{typeLabels[row.activity_type] || row.activity_type}</div>
          )}
        </div>
      ),
      sortable: true,
      primary: true,
    },
    {
      key: 'due_date',
      label: t('weekly.columns.when'),
      render: row => row.due_date
        ? new Date(row.due_date + 'T00:00:00').toLocaleDateString(i18n.language, { day: '2-digit', month: '2-digit' })
        : '—',
      sortable: true,
    },
    {
      key: 'field_name',
      label: t('weekly.columns.where'),
      render: row => row.field_name || '—',
      sortable: true,
    },
    {
      key: 'priority',
      label: t('weekly.columns.priority'),
      render: row => {
        const colors = { HIGH: 'text-red-500', MEDIUM: 'text-yellow-500', LOW: 'text-[var(--aif-text-muted)]' };
        return <span className={`text-xs font-medium ${colors[row.priority] || ''}`}>{row.priority || '—'}</span>;
      },
      sortable: true,
    },
    {
      key: 'source',
      label: t('weekly.columns.why'),
      render: row => <span className="text-xs text-[var(--aif-text-muted)]">{row.source || t('weekly.sourceManual')}</span>,
    },
  ], [t, typeLabels]);
}

function useTaskFilterDefs() {
  const { t } = useTranslation();
  const typeLabels = useTaskTypeLabels();
  return useMemo(() => [
    {
      key: '_status_group',
      options: [
        { value: '__all__', label: t('weekly.filters.all') },
        { value: 'open', label: t('weekly.filters.open'), filterFn: row => ['DRAFT', 'SUBMITTED'].includes(row.status) },
        { value: 'done', label: t('weekly.filters.done'), filterFn: row => ['APPROVED', 'POSTED'].includes(row.status) },
        {
          value: 'overdue',
          label: t('weekly.filters.overdue'),
          filterFn: row => {
            if (!row.due_date) return false;
            const today = new Date().toISOString().split('T')[0];
            return row.due_date < today && !['APPROVED', 'POSTED', 'ARCHIVED'].includes(row.status);
          },
        },
      ],
    },
    {
      key: 'activity_type',
      options: [
        { value: '__all__', label: t('weekly.filters.allTypes') },
        { value: 'FERTILIZATION', label: typeLabels.FERTILIZATION },
        { value: 'CROP_PROTECTION', label: typeLabels.CROP_PROTECTION },
        { value: 'TILLAGE', label: typeLabels.TILLAGE },
        { value: 'SOWING', label: typeLabels.SOWING },
        { value: 'HARVEST', label: typeLabels.HARVEST },
      ],
    },
  ], [t, typeLabels]);
}

function TaskDetailSheet({ task, onClose, onPatch, onDelete }) {
  const { t, i18n } = useTranslation();
  const typeLabels = useTaskTypeLabels();
  return (
    <Sheet.Root open={!!task} onOpenChange={(o) => !o && onClose()}>
      <Sheet.Content side="bottom" size="md" title={task?.title || ''}>
        <Sheet.Body>
      {task && <div className="space-y-3 pb-2">
        <div className="flex items-center gap-2 flex-wrap">
          <UnifiedStatusBadge status={task.status} />
          {task.priority && (
            <span className={`text-xs font-medium px-2 py-0.5 rounded-full
              ${task.priority === 'HIGH' ? 'bg-red-500/10 text-red-500'
                : task.priority === 'MEDIUM' ? 'bg-yellow-500/10 text-yellow-500'
                : 'bg-[var(--aif-bg-elevated)] text-[var(--aif-text-muted)]'}`}>
              {task.priority}
            </span>
          )}
        </div>
        <div className="grid grid-cols-2 gap-2 text-sm">
          {task.field_name && <div><div className="text-xs text-[var(--aif-text-muted)]">{t('weekly.columns.where')}</div><div>{task.field_name}</div></div>}
          {task.due_date && <div><div className="text-xs text-[var(--aif-text-muted)]">{t('weekly.columns.when')}</div>
            <div>{new Date(task.due_date + 'T00:00:00').toLocaleDateString(i18n.language)}</div></div>}
          {task.activity_type && <div><div className="text-xs text-[var(--aif-text-muted)]">{t('weekly.columns.type')}</div>
            <div>{typeLabels[task.activity_type] || task.activity_type}</div></div>}
          {task.source && <div><div className="text-xs text-[var(--aif-text-muted)]">{t('weekly.columns.why')}</div><div>{task.source}</div></div>}
        </div>
        {task.notes && (
          <div>
            <div className="text-xs text-[var(--aif-text-muted)] mb-1">{t('task.notesLabel')}</div>
            <p className="text-sm text-[var(--aif-text-primary)]">{task.notes}</p>
          </div>
        )}
        <StatusActions
          currentStatus={task.status}
          onTransition={(newStatus) => { onPatch(task.id, { status: newStatus }); onClose(); }}
        />
        <button
          onClick={() => { onDelete(task.id); onClose(); }}
          className="w-full py-2 rounded-xl border border-red-300 text-red-500 text-sm hover:bg-red-50 transition"
        >
          {t('weekly.deleteTask')}
        </button>
      </div>}
        </Sheet.Body>
      </Sheet.Content>
    </Sheet.Root>
  );
}

function TaskTableView({ token, onNewTask }) {
  const { t, i18n } = useTranslation();
  const taskColumns = useTaskColumns();
  const taskFilterDefs = useTaskFilterDefs();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const r = await apiClient.get('/api/v1/aif/farm/tasks?limit=200&sort=due_date&order=asc');
      const d = r.data;
      setTasks(d.items || d.tasks || []);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const handlePatch = useCallback(async (taskId, updates) => {
    try {
      await apiClient.patch(`/api/v1/aif/farm/tasks/${taskId}`, updates, {
        entityType: 'task',
        queueOnOffline: true,
      });
      load();
    } catch (err) {
      if (!(err instanceof ApiError)) throw err;
    }
  }, [load]);

  const handleDelete = useCallback(async (taskId) => {
    try {
      await apiClient.delete(`/api/v1/aif/farm/tasks/${taskId}`, {
        entityType: 'task',
        queueOnOffline: true,
      });
      load();
    } catch (err) {
      if (!(err instanceof ApiError)) throw err;
    }
  }, [load]);

  return (
    <div data-testid="task-table-view">
      <TableView
        columns={taskColumns}
        data={tasks}
        searchKeys={['title', 'field_name', 'notes']}
        defaultSort={{ key: 'due_date', direction: 'asc' }}
        filterDefs={taskFilterDefs}
        emptyState={{
          title: t('task.emptyTitle'),
          subtitle: t('task.emptyHint'),
          cta: onNewTask ? { label: `+ ${t('task.new')}`, onClick: onNewTask } : undefined,
        }}
        onRowClick={row => setSelected(row)}
        mobileCardRenderer={row => {
          const today = new Date().toISOString().split('T')[0];
          const overdue = row.due_date && row.due_date < today && !['APPROVED', 'POSTED', 'ARCHIVED'].includes(row.status);
          return (
            <div className={`bg-[var(--aif-bg-surface)] rounded-xl px-3 py-2.5 border
              ${overdue ? 'border-red-400/50' : 'border-[var(--aif-border-default)]'}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-[var(--aif-text-primary)] truncate">{row.title}</div>
                  <div className="text-xs text-[var(--aif-text-muted)] mt-0.5">
                    {row.field_name || '—'} · {row.due_date
                      ? new Date(row.due_date + 'T00:00:00').toLocaleDateString(i18n.language, { day: '2-digit', month: '2-digit' })
                      : t('task.noDate')}
                  </div>
                </div>
                <UnifiedStatusBadge status={row.status} />
              </div>
            </div>
          );
        }}
        loading={loading}
      />
      {selected && (
        <TaskDetailSheet
          task={selected}
          onClose={() => setSelected(null)}
          onPatch={handlePatch}
          onDelete={handleDelete}
        />
      )}
    </div>
  );
}

// ── Approval Queue View ──────────────────────────────────────────────────────

function ApprovalQueueView({ token, onStatusChange }) {
  const { t } = useTranslation();
  const [queue, setQueue] = useState(null);
  const [processing, setProcessing] = useState(null);

  const loadQueue = useCallback(async () => {
    try {
      const r = await apiClient.get('/api/v1/aif/tasks/approval-queue');
      setQueue(r.data?.tasks || []);
    } catch {
      setQueue([]);
    }
  }, []);

  useEffect(() => { if (token) loadQueue(); }, [token, loadQueue]);

  const handleApprove = async (taskId) => {
    setProcessing(taskId);
    try {
      await apiClient.post(`/api/v1/aif/tasks/approval-queue/${taskId}/approve`);
      await loadQueue();
      onStatusChange?.();
    } catch { /* handled by apiClient */ }
    setProcessing(null);
  };

  const handleReject = async (taskId) => {
    setProcessing(taskId);
    try {
      await apiClient.post(`/api/v1/aif/tasks/approval-queue/${taskId}/reject`);
      await loadQueue();
      onStatusChange?.();
    } catch { /* handled by apiClient */ }
    setProcessing(null);
  };

  if (queue === null) {
    return <div className="flex justify-center py-8"><div className="text-sm text-[var(--aif-text-muted)]">{t('ui.loading')}</div></div>;
  }

  if (queue.length === 0) {
    return (
      <div className="text-center py-8">
        <div className="text-2xl mb-2">✅</div>
        <div className="text-sm text-[var(--aif-text-muted)]">{t('approval.empty', 'Keine Aufgaben zur Freigabe')}</div>
      </div>
    );
  }

  return (
    <div className="space-y-2" data-testid="approval-queue">
      <div className="text-xs text-[var(--aif-text-muted)] px-1">{t('approval.count', { count: queue.length, defaultValue: `${queue.length} zur Freigabe` })}</div>
      {queue.map(task => (
        <div key={task.id} className="rounded-xl border border-[var(--aif-border-default)] bg-[var(--aif-bg-surface)] p-3 space-y-2">
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-[var(--aif-text-primary)] truncate">{task.title}</div>
              <div className="text-xs text-[var(--aif-text-muted)] space-x-2">
                {task.field_name && <span>📍 {task.field_name}</span>}
                {task.worker_name && <span>👤 {task.worker_name}</span>}
                {task.machine_name && <span>🚜 {task.machine_name}</span>}
              </div>
            </div>
            <UnifiedStatusBadge status={task.status} />
          </div>
          {(task.product_name || task.actual_quantity || task.duration_minutes) && (
            <div className="text-xs text-[var(--aif-text-secondary)] flex flex-wrap gap-2">
              {task.product_name && <span>{task.product_name}</span>}
              {task.actual_quantity && <span>{task.actual_quantity} {task.planned_unit || ''}</span>}
              {task.actual_area_ha && <span>{task.actual_area_ha} ha</span>}
              {task.duration_minutes && <span>⏱ {task.duration_minutes} min</span>}
            </div>
          )}
          <div className="flex gap-2">
            <button
              onClick={() => handleApprove(task.id)}
              disabled={processing === task.id}
              className="flex-1 min-h-[var(--aif-touch-min)] rounded-xl text-sm font-medium bg-[var(--aif-color-success)] text-white hover:opacity-90 transition disabled:opacity-50"
            >
              {t('approval.approve', 'Freigeben')}
            </button>
            <button
              onClick={() => handleReject(task.id)}
              disabled={processing === task.id}
              className="flex-1 min-h-[var(--aif-touch-min)] rounded-xl text-sm font-medium border border-[var(--aif-border-default)] text-[var(--aif-text-primary)] hover:bg-[var(--aif-bg-surface)] transition disabled:opacity-50"
            >
              {t('approval.reject', 'Zurückweisen')}
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────

export default function WeeklyPlan({ token, preselectedFieldId }) {
  const { t } = useTranslation();
  const userRole = getRoleFromToken(token);
  const [view, setView] = useState(() => {
    try { return localStorage.getItem('aif_weekly_view') || 'kalender'; } catch { return 'kalender'; }
  });
  const [weekly, setWeekly] = useState(null);
  const [fields, setFields] = useState([]);
  const [showForm, setShowForm] = useState(!!preselectedFieldId);

  const handleViewChange = (v) => {
    setView(v);
    try { localStorage.setItem('aif_weekly_view', v); } catch { /* ignore */ }
  };
  const [loading, setLoading] = useState(true);

  const loadWeekly = useCallback(async () => {
    if (!token) return;
    try {
      const r = await apiClient.get('/api/v1/aif/farm/tasks/weekly');
      setWeekly(r.data);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, [token]);

  const loadFields = useCallback(async () => {
    if (!token) return;
    try {
      const r = await apiClient.get('/api/v1/aif/farm/fields');
      const d = r.data;
      setFields(d.fields || d.data || []);
    } catch {
      // ignore
    }
  }, [token]);

  useEffect(() => {
    loadWeekly();
    loadFields();
  }, [loadWeekly, loadFields]);

  const handlePatch = useCallback(async (taskId, updates) => {
    try {
      await apiClient.patch(`/api/v1/aif/farm/tasks/${taskId}`, updates, {
        entityType: 'task',
        queueOnOffline: true,
      });
      loadWeekly();
    } catch (err) {
      if (!(err instanceof ApiError)) throw err;
    }
  }, [loadWeekly]);

  const handleDelete = useCallback(async (taskId) => {
    try {
      await apiClient.delete(`/api/v1/aif/farm/tasks/${taskId}`, {
        entityType: 'task',
        queueOnOffline: true,
      });
      loadWeekly();
    } catch (err) {
      if (!(err instanceof ApiError)) throw err;
    }
  }, [loadWeekly]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-sm text-[var(--aif-text-muted)]">{t('ui.loading')}</div>
      </div>
    );
  }

  const VIEWS = [
    { id: 'tabelle', label: t('weekly.viewTable') },
    { id: 'kalender', label: t('weekly.viewCalendar') },
    ...(APPROVAL_ROLES.has(userRole) ? [{ id: 'freigabe', label: t('weekly.viewApproval', 'Freigabe') }] : []),
  ];

  return (
    <div className="space-y-4" data-testid="weekly-plan" data-weekly-plan>
      {/* View switcher */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex gap-1 bg-[var(--aif-bg-elevated)] rounded-xl p-0.5" data-testid="view-switcher">
          {VIEWS.map(v => (
            <button
              key={v.id}
              onClick={() => handleViewChange(v.id)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition
                ${view === v.id
                  ? 'bg-[var(--aif-color-primary)] text-white shadow-sm'
                  : 'text-[var(--aif-text-secondary)] hover:text-[var(--aif-text-primary)]'
                }`}
              data-testid={`view-tab-${v.id}`}
            >
              {v.label}
            </button>
          ))}
        </div>
        <button
          onClick={() => setShowForm(x => !x)}
          className="text-xs font-medium text-[var(--aif-color-primary)] border border-[var(--aif-color-primary)] px-3 py-1.5 rounded-xl hover:bg-[var(--aif-color-primary)] hover:text-white transition"
          data-testid="new-task-button"
        >
          + {t('task.new')}
        </button>
      </div>

      {/* New task form (shown in both views) */}
      {showForm && (
        <TaskForm
          token={token}
          fields={fields}
          onSave={() => { setShowForm(false); loadWeekly(); }}
          onCancel={() => setShowForm(false)}
          initialFieldId={preselectedFieldId}
        />
      )}

      {/* ── Tabelle View ── */}
      {view === 'tabelle' && (
        <TaskTableView
          token={token}
          onNewTask={() => setShowForm(x => !x)}
        />
      )}

      {/* ── Freigabe (Approval Queue) View ── */}
      {view === 'freigabe' && (
        <ApprovalQueueView token={token} onStatusChange={loadWeekly} />
      )}

      {/* ── Kalender View (existing) ── */}
      {view === 'kalender' && (
        <>
          <SummaryBar summary={weekly?.summary} />

          {/* Overdue */}
          <OverdueSection
            tasks={weekly?.overdue || []}
            onPatch={handlePatch}
            onDelete={handleDelete}
          />

          {/* Suggested */}
          <SuggestedSection
            tasks={weekly?.suggested_tasks || []}
            token={token}
            onAdded={loadWeekly}
          />

          {/* 7-day columns — horizontal scroll on mobile */}
          <div data-testid="days-grid">
            <h3 className="text-sm font-semibold text-[var(--aif-text-primary)] mb-3">{t('weekly.thisWeek')}</h3>
            <div className="grid grid-cols-7 gap-2 min-w-[560px]">
              {(weekly?.days || []).map((day, i) => (
                <DayColumn
                  key={i}
                  day={day}
                  onPatch={handlePatch}
                  onDelete={handleDelete}
                  token={token}
                  onStatusTransition={loadWeekly}
                />
              ))}
            </div>
          </div>

          {/* Tasks without date */}
          {weekly?.no_date_tasks?.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-[var(--aif-text-muted)] mb-2">{t('task.noDate')}</h3>
              <AnimatedList className="space-y-2">
                {weekly.no_date_tasks.map(task => (
                  <TaskCard
                    key={task.id}
                    task={task}
                    onPatch={handlePatch}
                    onDelete={handleDelete}
                    weatherIcon={null}
                    token={token}
                    onStatusTransition={loadWeekly}
                  />
                ))}
              </AnimatedList>
            </div>
          )}
        </>
      )}
    </div>
  );
}
